import React, { useRef, useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import JSZip from "jszip";
import { ProteinViewer } from "@/components/protein-viewer";
import SubmitButton from "@minohealth/common/components/button/SubmitButton";
import useDialogOpenState from "@minohealth/common/hooks/useDialogOpenState";
import ChatImageViewer from "./ChatImageViewer";
import ChatImage from "../ChatImage";
import { getModalityLabel } from "../chat-report-panel/ReportFormDialog";
import { byIdentifier } from "@minohealth/common/utils/ids";
import { loadObjectFromSpaces, getSpacesPresignedUrl } from "@minohealth/common/services/SpacesService";
import { AGENT_USER, AI_USER, DEEP_RESEARCH_USER, useChatContext } from "@/services/chat-provider";
import { computeAgentBubbleData } from "../chat-bubble/utils";

import { AiOutlineFullscreen, AiOutlineArrowUp } from "react-icons/ai";
import { FaChevronRight, FaFilePdf, FaDownload, FaEye, FaCube, FaArrowUp, FaFolder, FaFolderOpen, FaDna, FaChevronDown, FaRegFolder } from "react-icons/fa";
import { RiExternalLinkLine } from "react-icons/ri";
import { BsFileEarmarkPdf, BsHexagon } from "react-icons/bs";
import { GiDna1, GiMolecule } from "react-icons/gi";
import { VscBeaker } from "react-icons/vsc";
import { IoDocumentTextOutline, IoFileTrayFullOutline } from "react-icons/io5";

import { DataFileViewer, isDataFile, getDataFileExt } from "./DataFileViewer";
import { FileText, FileJson, FileSpreadsheet, FileCode, X } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { useDocument } from "@/services/chat-provider/canvas";
import { OPEN_CANVAS_PANEL_EVENT } from "../canvas/CanvasPanel";
import { OPEN_REVIEW_PANEL_EVENT } from "../canvas/ReviewPanel";
import { Button } from "@/components/ui/button";
import { useReviewsForSession } from "@/services/chat-provider/reviewsQuery";
import "./chat-session-info.css";

const PROTEIN_EXTENSIONS = ['pdb', 'pdbqt', 'cif', 'mmcif', 'mol2', 'sdf'];

function isProteinFile(url) {
  if (!url) return false;
  const ext = url.split('.').pop()?.split('?')[0]?.toLowerCase();
  return PROTEIN_EXTENSIONS.includes(ext);
}

export default React.memo(function ChatSessionInfo({ isReportPanelOpen, startGeneratingReport, openReportPanel, isRightSidebarOpen = true, onToggleRightSidebar }) {
  const { useActiveChatSession, useActiveChatSessionImages, useActiveChatSessionReports, useActiveChatSessionId, useActiveChatMessages } = useChatContext()
  const [activeChatSessionId] = useActiveChatSessionId()

  const { data: activeChatSession } = useActiveChatSession()
  const { data: chatMessages = [] } = useActiveChatMessages()
  const { data: chatImages } = useActiveChatSessionImages()
  const { data: activeChatSessionReports } = useActiveChatSessionReports()

  useEffect(() => {
    Array.from(document.querySelectorAll("[data-patient-info-interactive]")).forEach((el) => {
      el.inert = !isRightSidebarOpen;
    });
  }, [isRightSidebarOpen]);

  const latestActiveSessionImage = (() => {
    if (activeChatSession?.images == null) return null;
    if (activeChatSession.images.length == 0) return null;
    const imageRef = activeChatSession.images[activeChatSession.images.length - 1];
    return chatImages?.find(byIdentifier(imageRef));
  })();


  // Process generated files and group them by triggering message
  const { generatedFiles, fileTree } = useMemo(() => {
    if (chatMessages.length === 0) return { generatedFiles: [], fileTree: [] }

    let allFiles = [];
    let tree = [];

    // Iterate through messages to find tool outputs and link them to the triggering user message
    let lastUserMessage = "Analysis";

    chatMessages.forEach((message, index) => {
      // Only capture text from HUMAN user messages (skip AI, agent, deep research)
      if (message.user !== AGENT_USER && message.user !== AI_USER && message.user !== DEEP_RESEARCH_USER) {
        // User messages store text in message.text
        let text = "";
        if (message.text) {
          text = message.text;
        } else if (typeof message.message === 'string') {
          text = message.message;
        } else if (Array.isArray(message.message)) {
          text = message.message.find(m => m.type === 'text' || m.data?.text)?.data?.text || "";
        }
        if (text) {
          lastUserMessage = text.replace(/\s+/g, ' ').trim();
          if (lastUserMessage.length > 60) {
            lastUserMessage = lastUserMessage.substring(0, 57) + "...";
          }
        }
        return;
      }

      // Skip non-agent messages (AI_USER, DEEP_RESEARCH_USER don't generate tool files)
      if (message.user !== AGENT_USER) return;

      if (message.message == null) return;

      const { allGeneratedFiles: messageFiles } = computeAgentBubbleData(message)

      if (messageFiles.length > 0) {
        const toolProjectName = messageFiles.find(f => f.projectName)?.projectName;
        const folderName = toolProjectName || lastUserMessage;

        // Build simple enriched files with clean names from URL
        const enrichedFiles = messageFiles.map((file, fileIdx) => {
          // Use the actual filename from the URL as display name
          const urlName = getFileNameFromUrl(file.url);
          let displayName = urlName || file.extractedName || `${file.type} ${fileIdx + 1}`;

          // Clean up display (replace underscores but keep extension)
          displayName = displayName.replace(/_/g, ' ');

          return {
            ...file,
            name: displayName,
            messageIndex: index,
            triggeringMessage: folderName
          };
        });

        allFiles.push(...enrichedFiles);

        // Simple grouping: one prompt folder with all files flat inside
        // Find or create the prompt folder (merge if same user message triggers multiple agent responses)
        let promptFolder = tree.find(node => node.name === folderName);
        if (!promptFolder) {
          promptFolder = {
            id: `prompt-${index}`,
            name: folderName,
            type: 'promptFolder',
            toolCategory: messageFiles[0]?.toolCategory || 'Other',
            children: [],
            messageIndex: index,
            // Collect all file URLs for zip download
            allFileUrls: []
          };
          tree.push(promptFolder);
        }

        enrichedFiles.forEach(file => {
          promptFolder.children.push({
            id: file.url,
            name: file.name,
            type: 'file',
            fileType: file.type,
            url: file.url,
            messageIndex: index
          });
          promptFolder.allFileUrls.push({ url: file.url, name: file.name });
        });
      }
    });

    return { generatedFiles: allFiles, fileTree: tree };
  }, [chatMessages]);

  const hasImages = activeChatSession?.images != null && activeChatSession.images.length > 0;
  const hasReports = activeChatSessionReports != null && activeChatSessionReports.length > 0;
  const hasReviews = activeChatSession?.canvases != null && activeChatSession.canvases.length > 0;
  const hasUploadedFiles = activeChatSession?.files != null && activeChatSession.files.length > 0;
  // Patient info is always there potentially, but we only care about artifacts for visibility
  const hasArtifacts = hasImages || hasReports || hasReviews || hasUploadedFiles || generatedFiles.length > 0;

  if (activeChatSession == null || isReportPanelOpen || !hasArtifacts) {
    return null;
  }

  return (
    <div className="chat-session-info" data-expanded-on-desktop={isRightSidebarOpen}>
      <div className="chat-session-info__body" data-patient-info-interactive>
        <ChatSessionInfoContent
          key={activeChatSession?._id}
          generatedFiles={generatedFiles}
          fileTree={fileTree}
          hasImages={hasImages}
          hasReports={hasReports}
          hasReviews={hasReviews}
          hasUploadedFiles={hasUploadedFiles}
          openReportPanel={openReportPanel}
          startGeneratingReport={startGeneratingReport}
          onToggleRightSidebar={onToggleRightSidebar}
        />
      </div>
    </div>
  );
})

function ChatSessionInfoContent({ generatedFiles, fileTree, hasImages, hasReports, hasReviews, hasUploadedFiles, startGeneratingReport, openReportPanel, onToggleRightSidebar }) {
  const { useActiveChatSession, useActiveChatSessionImages, useActiveChatSessionReports } = useChatContext()
  const { user } = useSelector((state) => state.app.datastate)

  const { data: activeChatSession } = useActiveChatSession()
  const { data: chatImages } = useActiveChatSessionImages()
  const { data: activeChatSessionReports } = useActiveChatSessionReports()
  const patientInfo = activeChatSession.patientInfo;

  // Reviews attached to canvases in this session, keyed by documentId, so a
  // canvas created via the review flow opens its report instead of the raw document.
  const { data: sessionReviews } = useReviewsForSession(hasReviews ? activeChatSession._id : null);
  const reviewsByDocumentId = useMemo(() => {
    const map = new Map();
    (sessionReviews ?? []).forEach((review) => map.set(review.documentId, review));
    return map;
  }, [sessionReviews]);

  const hasFilesTab = generatedFiles.length > 0 || hasReviews;

  // Determine initial active tab based on content
  const [activeTab, setActiveTab] = useState(() => {
    if (hasImages) return 'uploads';
    if (hasFilesTab) return 'files';
    if (hasReports) return 'reports';
    if (hasReviews) return 'reviews';
    if (hasUploadedFiles) return 'documents';
    return 'uploads'; // Default fallback
  });

  // Re-evaluate active tab if content changes and current tab is empty
  useEffect(() => {
    if (activeTab === 'uploads' && !hasImages) {
      if (hasFilesTab) setActiveTab('files');
      else if (hasReports) setActiveTab('reports');
      else if (hasReviews) setActiveTab('reviews');
      else if (hasUploadedFiles) setActiveTab('documents');
    }
  }, [hasImages, generatedFiles.length, hasReports, hasReviews, hasUploadedFiles]);


  // All protein file URLs across the entire session for the viewer
  const allProteinFileUrls = useMemo(
    () => generatedFiles.filter(f => isProteinFile(f.url)).map(f => f.url),
    [generatedFiles]
  );

  // Image preview functionality
  const previewImageDialogRef = useRef(null);
  const [shouldImageDialogOpen, setShouldImageDialogOpen] = useDialogOpenState(previewImageDialogRef);
  const [previewImageId, setPreviewImageId] = useState("");

  function previewImage(imageId) {
    setPreviewImageId(imageId);
    setShouldImageDialogOpen(true);
  }

  function stopImagePreview() {
    setShouldImageDialogOpen(false);
    setPreviewImageId("");
  }

  const handleDownload = async (file) => {
    const presignedUrl = await getSpacesPresignedUrl({ objectUrl: file.url, userToken: user.userToken });
    const response = await fetch(presignedUrl);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(blobUrl);
  };

  const [zippingFolderId, setZippingFolderId] = useState(null);

  const handleDownloadAllAsZip = async (promptFolder) => {
    if (zippingFolderId || !promptFolder?.allFileUrls?.length) {
      return;
    }

    setZippingFolderId(promptFolder.id);

    try {
      const zip = new JSZip();

      // Download all files and add to zip
      const filePromises = promptFolder.allFileUrls.map(async (fileInfo) => {
        try {
          const base64 = await loadObjectFromSpaces({ objectUrl: fileInfo.url, userToken: user.userToken });
          if (base64) {
            zip.file(fileInfo.name.replace(/ /g, '_'), base64, { base64: true });
          } else {
            console.warn('Empty file received for:', fileInfo.name);
          }
        } catch (e) {
          console.error(`Failed to download ${fileInfo.name}:`, e);
        }
      });

      await Promise.all(filePromises);

      const blob = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      const url = URL.createObjectURL(blob);
      a.href = url;

      const safeName = promptFolder.name
        .replace(/[^a-z0-9]/gi, '_')
        .replace(/_{2,}/g, '_')
        .substring(0, 50);

      a.download = `${safeName || 'generated_files'}.zip`;
      document.body.appendChild(a);
      a.click();
      console.log('Download triggered for:', a.download);

      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);

    } catch (e) {
      console.error('Failed to create zip:', e);
      alert('Failed to generate ZIP file: ' + e.message);
    } finally {
      setZippingFolderId(null);
    }
  };

  const jumpToChat = (messageIndex) => {
    if (messageIndex === undefined || messageIndex === null) return;
    const element = document.getElementById(`chat-message-${messageIndex}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('highlight-message');
      setTimeout(() => element.classList.remove('highlight-message'), 2000);
    }
  };

  const jumpToImageChat = (e, imageId) => {
    e.stopPropagation();
    const index = chatMessages.findIndex(msg => {
      if (msg.image) {
        return msg.image === imageId || msg.image?._id === imageId;
      }
      return false;
    });
    if (index !== -1) jumpToChat(index);
  }


  return (
    <div className="chat-session-info__content chat-session-info__content--tabbed">
      <div className="chat-session-info__header">
        <div className="chat-session-info__tabs">
          {hasImages && (
            <button
              className={`chat-session-info__tab ${activeTab === 'uploads' ? 'active' : ''}`}
              onClick={() => setActiveTab('uploads')}
            >
              Uploads
            </button>
          )}
          {hasFilesTab && (
            <button
              className={`chat-session-info__tab ${activeTab === 'files' ? 'active' : ''}`}
              onClick={() => setActiveTab('files')}
            >
              Artifacts
            </button>
          )}
          {hasReports && (
            <button
              className={`chat-session-info__tab ${activeTab === 'reports' ? 'active' : ''}`}
              onClick={() => setActiveTab('reports')}
            >
              Reports
            </button>
          )}
          {hasReviews && (
            <button
              className={`chat-session-info__tab ${activeTab === 'reviews' ? 'active' : ''}`}
              onClick={() => setActiveTab('reviews')}
            >
              Reviews
            </button>
          )}
          {hasUploadedFiles && (
            <button
              className={`chat-session-info__tab ${activeTab === 'documents' ? 'active' : ''}`}
              onClick={() => setActiveTab('documents')}
            >
              Documents
            </button>
          )}
          {/* Show at least one tab or empty msg if nothing? Usually hasArtifacts check at parent handles this */}
          {!hasImages && generatedFiles.length === 0 && !hasReports && !hasReviews && !hasUploadedFiles && (
            <div className="text-muted-foreground text-sm text-center py-2 w-full">No Artifacts</div>
          )}
        </div>
        {onToggleRightSidebar && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
            aria-label="Close panel"
            title="Close panel"
            onClick={() => onToggleRightSidebar(false)}
          >
            <X size={16} />
          </Button>
        )}
      </div>

      {activeTab === 'uploads' && hasImages && (
        <div className="chat-session-info__images-grid">
          {activeChatSession.images.map((imageId) => {
            const image = chatImages?.find(byIdentifier(imageId));
            return (
              <div key={imageId} className="chat-session-info__image-card" onClick={() => previewImage(imageId)}>
                <div className="chat-session-info__image-card-preview">
                  <ChatImage srcImage={image} thumbnail alt="chat image" />
                </div>
                <div className="chat-session-info__image-card-overlay">
                  <button
                    className="chat-session-info__jump-btn"
                    onClick={(e) => jumpToImageChat(e, imageId)}
                    title="Jump to chat"
                  >
                    <FaArrowUp size={12} style={{ transform: 'rotate(45deg)' }} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'files' && hasFilesTab && (
        <div className="chat-session-info__files-tree custom-scrollbar">
          {generatedFiles.length > 0 && fileTree.map(node => (
            <TreeNode key={node.id} node={node} jumpToChat={jumpToChat} handleDownload={handleDownload} handleDownloadAllAsZip={handleDownloadAllAsZip} zippingFolderId={zippingFolderId} allProteinFileUrls={allProteinFileUrls} userToken={user.userToken} />
          ))}
          {hasReviews && (
            <ul className="chat-session-info__review-list">
              {[...activeChatSession.canvases]
                .sort((a, b) => new Date(b.updatedAt ?? 0) - new Date(a.updatedAt ?? 0))
                .map((canvas) => (
                  <ReviewRow
                    key={canvas.documentId}
                    canvas={canvas}
                    review={reviewsByDocumentId.get(canvas.documentId) ?? null}
                    onOpenDocument={() => onToggleRightSidebar?.(false)}
                  />
                ))}
            </ul>
          )}
        </div>
      )}

      {activeTab === 'reports' && hasReports && (
        <div className="chat-session-info__reports-list">
          <ul className="chat-session-info__report-list">
            {activeChatSessionReports.map((report) => (
              <li key={report._id ?? report._clientId}>
                <ReportCard
                  images={chatImages}
                  report={report}
                  session={activeChatSession}
                  previewReport={() => {
                    openReportPanel({ type: 'ref', ref: report._id ?? report._clientId });
                  }}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {activeTab === 'reviews' && hasReviews && (
        <div className="chat-session-info__reviews-list">
          <ul className="chat-session-info__review-list">
            {[...activeChatSession.canvases]
              .sort((a, b) => new Date(b.updatedAt ?? 0) - new Date(a.updatedAt ?? 0))
              .map((canvas) => (
                <ReviewRow key={canvas.documentId} canvas={canvas} />
              ))}
          </ul>
        </div>
      )}

      {activeTab === 'documents' && hasUploadedFiles && (
        <div className="chat-session-info__reviews-list">
          <ul className="chat-session-info__review-list">
            {activeChatSession.files.map((file) => (
              <li key={file._id ?? file.name} className="chat-session-info__tab flex items-center gap-2 w-full">
                <FileText size={14} className="text-muted-foreground shrink-0" />
                <span className="truncate">{file.name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {activeChatSession.patientInfo != null && (
        <div className="chat-session-info__patient-info mt-6 border-t border-border pt-4">
          <h3 className="chat-session-info__section-title mb-2">Patient Information</h3>
          <div className="chat-session-info__patient-info-item">
            <p>Patient Name</p>
            <p>{patientInfo.name}</p>
          </div>

          <div className="chat-session-info__patient-info-item">
            <p>Patient ID</p>
            <p>{patientInfo.id}</p>
          </div>

          <div className="diagnose-chat__patient-inline-info">
            <div className="chat-session-info__patient-info-item">
              <p>Gender</p>
              <p>{patientInfo.gender}</p>
            </div>
            <div className="chat-session-info__patient-info-item">
              <p>Patient Age</p>
              <p>{patientInfo.age}</p>
            </div>
          </div>
        </div>
      )}

      <ChatImageViewer
        srcImageId={previewImageId}
        onCloseViewer={stopImagePreview}
        ref={previewImageDialogRef}
        onGenerateReport={(image) => {
          startGeneratingReport(activeChatSession._id, [{
            _id: image._id,
            _clientId: image._clientId,
          }]);
        }}
        showGenerateReportBtn={true}
      />
    </div>
  );
}

// Recursive Tree Node Component
function TreeNode({ node, level = 0, jumpToChat, handleDownload, handleDownloadAllAsZip, zippingFolderId, allProteinFileUrls = [], userToken }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [proteinViewerOpen, setProteinViewerOpen] = useState(false);
  const [dataViewerOpen, setDataViewerOpen] = useState(false);

  // File node (leaf)
  if (node.type === 'file') {
    const isProtein = isProteinFile(node.url);
    const isData = !isProtein && isDataFile(node.url);

    const fileIcon = isProtein
      ? <GiMolecule className="text-primary" />
      : isData
        ? (() => {
            const ext = getDataFileExt(node.url);
            if (ext === 'json') return <FileJson className="text-blue-400 w-4 h-4" />;
            if (ext === 'csv') return <FileSpreadsheet className="text-green-400 w-4 h-4" />;
            if (ext === 'xml') return <FileCode className="text-orange-400 w-4 h-4" />;
            return <FileText className="text-sky-400 w-4 h-4" />;
          })()
        : <IoDocumentTextOutline className="text-muted-foreground" />;

    const rowClickHandler = isProtein
      ? () => setProteinViewerOpen(true)
      : isData
        ? () => setDataViewerOpen(true)
        : (e) => { e.stopPropagation(); jumpToChat(node.messageIndex); };

    const rowTitle = isProtein
      ? "Click to view 3D structure"
      : isData
        ? "Click to view file"
        : "Click to jump to chat";

    const fileNode = (
      <div
        className="tree-node file-node relative"
        style={{ paddingLeft: `${level * 1.2 + 0.5}rem` }}
        onClick={rowClickHandler}
        title={rowTitle}
      >
        <span className="tree-node__icon">{fileIcon}</span>
        <span className="tree-node__label truncate text-muted-foreground">{node.name}</span>
        {(isProtein || isData) && (
          <button
            className="tree-node__action-btn"
            onClick={(e) => { e.stopPropagation(); jumpToChat(node.messageIndex); }}
            title="Jump to chat"
          >
            <FaArrowUp size={10} style={{ transform: 'rotate(45deg)' }} />
          </button>
        )}
        <button
          className="tree-node__action-btn"
          onClick={(e) => { e.stopPropagation(); handleDownload(node); }}
          title="Download"
        >
          <FaDownload size={10} />
        </button>
      </div>
    );

    if (isData) {
      return (
        <>
          {fileNode}
          <DataFileViewer
            url={node.url}
            name={node.name}
            userToken={userToken}
            open={dataViewerOpen}
            onOpenChange={setDataViewerOpen}
          />
        </>
      );
    }

    if (!isProtein) return fileNode;

    return (
      <ProteinViewer
        fileUrls={allProteinFileUrls}
        defaultFileUrl={node.url}
        open={proteinViewerOpen}
        onOpenChange={setProteinViewerOpen}
      >
        {fileNode}
      </ProteinViewer>
    );
  }

  // Prompt folder node
  return (
    <div className="tree-node-wrapper prompt-folder-wrapper relative">
      <div
        className={`tree-node folder-node prompt-folder ${isExpanded ? 'active' : ''}`}
        style={{ paddingLeft: `${level * 1}rem` }}
        onClick={(e) => { e.stopPropagation(); setIsExpanded(v => !v); }}
      >
        <span className="tree-node__toggle text-muted-foreground">
          {isExpanded ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
        </span>
        <span className="tree-node__icon">
          <VscBeaker className="text-primary" />
        </span>
        <span className="tree-node__label truncate font-medium text-foreground" title={node.name}>
          {node.name}
        </span>
        {node.allFileUrls?.length > 0 && (
          <button
            className="tree-node__zip-btn"
            onClick={(e) => { e.stopPropagation(); handleDownloadAllAsZip(node); }}
            title={zippingFolderId === node.id ? "Creating zip..." : "Download all as ZIP"}
            disabled={zippingFolderId != null}
          >
            {zippingFolderId === node.id ? <span className="tree-node__zip-spinner" /> : <FaDownload size={11} />}
          </button>
        )}
      </div>
      {isExpanded && node.children && (
        <div className="tree-node__children relative">
          <div className="tree-line-vertical" style={{ left: `${level * 1 + 0.75}rem` }}></div>
          {node.children.map(child => (
            <TreeNode key={child.id} node={child} level={level + 1} jumpToChat={jumpToChat} handleDownload={handleDownload} handleDownloadAllAsZip={handleDownloadAllAsZip} zippingFolderId={zippingFolderId} allProteinFileUrls={allProteinFileUrls} userToken={userToken} />
          ))}
        </div>
      )}
    </div>
  );
}


function getFileNameFromUrl(url) {
  try {
    return new URL(url).pathname.split('/').pop() || url;
  } catch {
    return url.split('/').pop()?.split('?')[0] || url;
  }
}

function ReviewRow({ canvas, review, onOpenDocument }) {
  const { data: canvasDoc } = useDocument(canvas.documentId);
  const title = canvasDoc?.title ?? canvas.title ?? "Untitled";
  const pendingDiffCount = (canvasDoc?.pendingDiffs ?? []).filter((d) => d.status === "pending").length;

  const openRow = () => {
    if (review) {
      document.dispatchEvent(
        new CustomEvent(OPEN_REVIEW_PANEL_EVENT, { detail: { reviewId: review._id } })
      );
      return;
    }
    document.dispatchEvent(
      new CustomEvent(OPEN_CANVAS_PANEL_EVENT, { detail: { documentId: canvas.documentId } })
    );
    onOpenDocument?.();
  };

  const reviewBadge = review && (review.status === "pending" || review.status === "processing")
    ? "Reviewing..."
    : review?.status === "failed"
      ? "Failed"
      : null;

  return (
    <li>
      <button
        type="button"
        className="chat-session-info__review-row"
        onClick={openRow}
      >
        <IoDocumentTextOutline className="text-muted-foreground shrink-0" />
        <span className="chat-session-info__review-row-title truncate">{title}</span>
        {reviewBadge ? (
          <Badge variant="secondary">{reviewBadge}</Badge>
        ) : pendingDiffCount > 0 ? (
          <Badge variant="secondary">{pendingDiffCount}</Badge>
        ) : null}
      </button>
    </li>
  );
}

function ReportCard({ report, images, previewReport }) {
  const reportImage = images?.find(byIdentifier(report.image))

  return (
    <div className="chat-session-info__report-card">
      <ChatImage srcImage={reportImage} thumbnail className="chat-session-info__report-card-image" />
      <div className="chat-session-info__report-card-info">
        <p>{report.patientInfo?.name}</p>
        <p>{getModalityLabel(report.modality)}</p>
      </div>

      <button className="chat-session-info__report-card-open-btn" onClick={previewReport}>
        <RiExternalLinkLine />
        <span className="sr-only">Open report</span>
      </button>
    </div>
  );
}
